import RepoTypeIcon from 'components/RepoTypeIcon';
import Tooltip from 'components/Tooltip';
import { truncateText } from 'lib/helper';
import React, { Fragment } from 'react';

import BuildLogsDisplayDialog from './BuildLogsDisplayDialog';

interface RowData {
  id: string;
  name: string;
  branch: string;
  account: {
    host_type: string;
  };
  organization?: string;
  project?: string;
  group_name?: string;
  workspace?: string;
  languages?: {
    supported_languages?: string[];
    unsupported_languages?: string[];
  };
  is_partially_scanned?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scan: any;
}

interface Props {
  rowData: RowData;
}

const RepositoryBranchColumnContents: React.FC<Props> = ({ rowData }) => {
  // Inline method to check if a warning needs to be rendered
  const renderLanguagesWarning = (): boolean => {
    return (
      (rowData?.languages?.unsupported_languages &&
        rowData?.languages?.unsupported_languages?.length > 0) ||
      rowData?.is_partially_scanned ||
      false
    );
  };

  return (
    <div className="pr-4">
      <Tooltip
        tooltip={<RepositoryBranchTooltip rowData={rowData} />}
        title="Repository/Branch"
      >
        <RepoTypeIcon repoType={rowData['account']['host_type']} size={24} />
        &nbsp;&nbsp;
        <span>
          <strong>
            {
              truncateText(`${rowData.name}/${rowData.branch}`, 40).split(
                '/',
              )[0]
            }
          </strong>
          {truncateText(`${rowData.name}/${rowData.branch}`, 40).includes(
            '/',
          ) && (
            <>
              /
              {
                truncateText(`${rowData.name}/${rowData.branch}`, 40).split(
                  '/',
                )[1]
              }
            </>
          )}
        </span>
        {renderLanguagesWarning() && (
          <span
            className="ml-2"
            style={{ cursor: 'pointer', color: '#ffc107' }}
            id={rowData.id}
          >
            <i className="fas fa-exclamation-triangle fa-lg" />
          </span>
        )}
      </Tooltip>
      {rowData?.scan?.has_build_logs && (
        <Tooltip
          tooltip="One or more scanners failed. Click for details..."
          title="Scan Errors"
        >
          <span
            className="mr-2"
            style={{ cursor: 'pointer', color: '#ffc107' }}
            id={rowData.id}
          >
            <BuildLogsDisplayDialog repositoryId={parseInt(rowData.id)} />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const RepositoryBranchTooltip: React.FC<Props> = ({ rowData }) => {
  const unsupportedLanguagesLength = (): number => {
    return (
      (rowData?.languages?.unsupported_languages &&
        rowData?.languages?.unsupported_languages?.length) ||
      0
    );
  };

  const supportedLanguagesLength = (): number => {
    return (
      (rowData?.languages?.supported_languages &&
        rowData?.languages?.supported_languages?.length) ||
      0
    );
  };

  return (
    <Fragment>
      <strong>Type:</strong>&nbsp;&nbsp;
      <RepoTypeIcon repoType={rowData['account']['host_type']} size={24} />
      <br />
      <strong>Repository: </strong>
      {rowData.name}
      <br />
      <strong>Branch: </strong>
      {rowData.branch}
      {rowData.organization && (
        <>
          <br />
          <strong>Organization: </strong>
          {rowData.organization}
        </>
      )}
      {rowData.project && (
        <>
          <br />
          <strong>Project: </strong>
          {rowData.project}
        </>
      )}
      {rowData.group_name && (
        <>
          <br />
          <strong>Group: </strong>
          {rowData.group_name}
        </>
      )}
      {rowData.workspace && (
        <>
          <br />
          <strong>Workspace: </strong>
          {rowData.workspace}
        </>
      )}
      {(rowData.languages?.supported_languages ||
        rowData.is_partially_scanned) && <hr />}
      {rowData.is_partially_scanned && (
        <div style={{ backgroundColor: '#ffc107', padding: '0px 2px' }}>
          Repository/branch is partially scanned.
        </div>
      )}
      {supportedLanguagesLength() > 0 && (
        <>
          <strong>Languages Scanned: </strong>
          {rowData?.languages?.supported_languages?.map((lang, index) => (
            <span key={index}>
              {lang}
              {index < supportedLanguagesLength() - 1 ? ', ' : ''}
            </span>
          ))}
        </>
      )}
      {unsupportedLanguagesLength() > 0 && (
        <>
          <br />
          <div style={{ backgroundColor: '#ffc107', padding: '0px 2px' }}>
            <strong>Languages Not Scanned: </strong>
            {rowData?.languages?.unsupported_languages?.map((lang, index) => (
              <span key={index}>
                {lang}
                {index < unsupportedLanguagesLength() - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </>
      )}
    </Fragment>
  );
};

export default RepositoryBranchColumnContents;
